// v0.30 — the live EarnSource. Reads the customer's M-MMF position straight
// from chain (balanceOf × index) and submits subscribe / redeem as UserOps
// through the same Circle bundler path as a USDC send. No Worker on the read
// OR the write path — the Worker only publishes the rebase index (and serves
// /earn/config for the APR). Swapping this in for placeholderEarnSource is a
// one-line change in dropCatchView; the sheet UI does not move.

import { encodeFunctionData, formatUnits, parseUnits } from "viem";
import type { WalletAdapter } from "./WalletAdapter";
import type { EarnMove, EarnPosition, EarnSource } from "../earn";
import { recordTx } from "../api"; // v0.90.26 — Earn legs recorded like any other

const USDC_DECIMALS = 6;
const MMF_DECIMALS = 6; // display decimals; matches USDC

// Minimal ABIs — only the functions we call.
const MMF_ABI = [
  {
    type: "function",
    name: "balanceOf",
    stateMutability: "view",
    inputs: [{ name: "a", type: "address" }],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "index",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
] as const;

const TELLER_ABI = [
  {
    type: "function",
    name: "subscribe",
    stateMutability: "nonpayable",
    inputs: [{ name: "amount", type: "uint256" }],
    outputs: [],
  },
  {
    type: "function",
    name: "redeem",
    stateMutability: "nonpayable",
    inputs: [{ name: "amount", type: "uint256" }],
    outputs: [],
  },
] as const;

const USDC_ABI = [
  {
    type: "function",
    name: "approve",
    stateMutability: "nonpayable",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ type: "bool" }],
  },
] as const;

export interface TellerEarnConfig {
  mmfAddress: string;
  tellerAddress: string;
  usdcAddress: string;
  /** Base URL for /earn/config (the Worker). */
  apiBase: string;
}

export class TellerEarnSource implements EarnSource {
  constructor(
    private readonly adapter: WalletAdapter,
    private readonly cfg: TellerEarnConfig,
  ) {}

  async getPosition(address: string): Promise<EarnPosition> {
    const rawBal = await this.adapter.readUint(
      this.cfg.mmfAddress,
      MMF_ABI,
      "balanceOf",
      [address],
    );
    // balanceOf already returns the index-scaled balance (6dp), and the $1 peg
    // makes the USD value 1:1 with it — so no index() read is needed here.
    const usd = Number(formatUnits(rawBal, MMF_DECIMALS));
    return { units: usd, navPerUnit: 1, usd };
  }

  async getApy(): Promise<number | null> {
    try {
      const res = await fetch(`${this.cfg.apiBase}/earn/config`);
      if (!res.ok) return null;
      const j = (await res.json()) as { aprPct?: number; stale?: boolean };
      // If the publisher is stale we still show the last-known rate; the config
      // endpoint returns the on-chain-derived number regardless.
      return typeof j.aprPct === "number" ? j.aprPct : null;
    } catch {
      return null;
    }
  }

  /** v0.90.26 — record at acceptance, again at the receipt; return at acceptance. */
  private track(address: string, sub: { userOpHash: string; settled: Promise<{ txHash: string; success: boolean }> }, kind: "earn_in" | "earn_out", usd: number): EarnMove {
    const base = { address, userOpHash: sub.userOpHash, direction: "send" as const, amount: usd.toFixed(2), counterparty: this.cfg.tellerAddress, currency: "USD", kind };
    recordTx(base).catch(() => {});
    const settled = sub.settled
      .then(({ txHash, success }) => {
        recordTx({ ...base, txHash, success }).catch(() => {});
        return success;
      })
      .catch(() => false);
    return { settled };
  }

  async moveToEarning(address: string, usd: number): Promise<EarnMove> {
    const amount = parseUnits(usd.toFixed(USDC_DECIMALS), USDC_DECIMALS);
    // approve(teller, amount) + subscribe(amount) in ONE UserOp → one Face ID.
    const sub = await this.adapter.submit([
      {
        to: this.cfg.usdcAddress,
        data: encodeFunctionData({
          abi: USDC_ABI,
          functionName: "approve",
          args: [this.cfg.tellerAddress as `0x${string}`, amount],
        }),
      },
      {
        to: this.cfg.tellerAddress,
        data: encodeFunctionData({
          abi: TELLER_ABI,
          functionName: "subscribe",
          args: [amount],
        }),
      },
    ]);
    return this.track(address, sub, "earn_in", usd);
  }

  async moveToCash(address: string, usd: number): Promise<EarnMove> {
    const amount = parseUnits(usd.toFixed(MMF_DECIMALS), MMF_DECIMALS);
    const redeem = {
      to: this.cfg.tellerAddress,
      data: encodeFunctionData({
        abi: TELLER_ABI,
        functionName: "redeem",
        args: [amount],
      }),
    };
    const sub = await this.adapter.submit([redeem]);
    return this.track(address, sub, "earn_out", usd);
  }
}
